// Offline convolution of a dry source against each room IR.
//
// Each state's IR is rendered through an OfflineAudioContext + ConvolverNode in
// parallel. The reverb tail's own energy (denser/longer in an untreated room)
// makes the convolved output measurably louder or quieter than the treated
// room even when both IRs share the same peak target — so states are then
// loudness-matched (RMS) against their shared average before a per-state
// clip-protect pass. Matching loudness, not peak, is what makes an A/B
// listening comparison actually about tone rather than volume.

/** A rendered, convolved buffer plus the peak/RMS it reached before normalizing. */
interface Rendered {
  state: string;
  buffer: AudioBuffer;
  peak: number;
  rms: number;
}

/** RMS across every channel/sample of a rendered buffer (loudness proxy). */
function bufferRms(buffer: AudioBuffer): number {
  let sumSq = 0;
  let count = 0;
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    const ch = buffer.getChannelData(c);
    for (let i = 0; i < ch.length; i++) sumSq += ch[i] * ch[i];
    count += ch.length;
  }
  return count > 0 ? Math.sqrt(sumSq / count) : 0;
}

/**
 * Per-state gain that (1) matches every state's RMS to the group's average,
 * then (2) pulls that state back down if the loudness match would clip it.
 * Pure/synchronous so it can be unit-tested without a real OfflineAudioContext.
 */
export function computeNormalizationScales(
  results: Array<{ state: string; peak: number; rms: number }>,
): Record<string, number> {
  const avgRms = results.reduce((sum, r) => sum + r.rms, 0) / Math.max(results.length, 1);
  const scales: Record<string, number> = {};
  for (const { state, peak, rms } of results) {
    let scale = avgRms > 1e-8 && rms > 1e-8 ? avgRms / rms : 1.0;
    const projectedPeak = peak * scale;
    if (projectedPeak > 0.95) scale *= 0.9 / projectedPeak;
    scales[state] = scale;
  }
  return scales;
}

/**
 * Convolve `source` against every IR in `irs`, returning one wet buffer per
 * state id. States whose render fails are omitted.
 */
export async function convolveAll(
  source: AudioBuffer,
  irs: Record<string, AudioBuffer>,
): Promise<Record<string, AudioBuffer>> {
  const states = Object.keys(irs);
  if (states.length === 0) return {};

  const jobs = states.map(async (state): Promise<Rendered | null> => {
    const ir = irs[state];
    try {
      const offline = new OfflineAudioContext(
        source.numberOfChannels,
        source.length + ir.length,
        source.sampleRate,
      );
      const convolver = offline.createConvolver();
      convolver.normalize = false; // we manage levels ourselves
      convolver.buffer = ir;
      const src = offline.createBufferSource();
      src.buffer = source;
      src.connect(convolver);
      convolver.connect(offline.destination);
      src.start(0);

      const rendered = await offline.startRendering();
      let peak = 0;
      for (let c = 0; c < rendered.numberOfChannels; c++) {
        const ch = rendered.getChannelData(c);
        for (let i = 0; i < ch.length; i++) {
          const a = Math.abs(ch[i]);
          if (a > peak) peak = a;
        }
      }
      return { state, buffer: rendered, peak, rms: bufferRms(rendered) };
    } catch {
      return null;
    }
  });

  const results = (await Promise.all(jobs)).filter((r): r is Rendered => r !== null);
  if (results.length === 0) return {};

  // Loudness-match every state to the group's average RMS, then clip-protect
  // each one independently — a state loud enough to need it shouldn't force
  // the others quieter, and a state made louder by the match shouldn't clip.
  const scales = computeNormalizationScales(results);

  const out: Record<string, AudioBuffer> = {};
  for (const { state, buffer } of results) {
    const scale = scales[state];
    if (scale !== 1.0) {
      for (let c = 0; c < buffer.numberOfChannels; c++) {
        const ch = buffer.getChannelData(c);
        for (let i = 0; i < ch.length; i++) ch[i] *= scale;
      }
    }
    out[state] = buffer;
  }
  return out;
}
